import React from "react";

import {
    FRAG_BLUR,
    FRAG_FLAT,
    GLCanvas,
    type GLProgram,
    type GLSize,
    GL_BG,
    GL_RENDER,
    type PoleBuffers,
    type RenderTarget,
    bindRenderTarget,
    blurChain,
    createFullscreenVAO,
    createPoleBuffers,
    createProgram,
    createRenderTarget,
    deleteRenderTarget,
    drawFullscreen,
    gradientSource,
    hexToRgb,
    simulatePoles,
    sizePassTargets,
} from "../../shared/gl";
import { HALO, HALO_MOTION, HALO_POLES } from "./halo";

const TAU = Math.PI * 2;
const BG = hexToRgb(GL_BG);

interface Cursor {
    x: number;
    y: number;
    tx: number;
    ty: number;
    amt: number;
    want: number;
    seen: boolean;
}

interface Scene {
    grad: GLProgram;
    blur: GLProgram;
    flat: GLProgram;
    vao: WebGLVertexArrayObject;
    targets: { grad: RenderTarget; ping: RenderTarget; pong: RenderTarget };
    poles: PoleBuffers;
    size: GLSize | null;
    t: number;
}

const onInit = (gl: WebGL2RenderingContext): Scene => ({
    grad: createProgram(gl, gradientSource(HALO_POLES.length)),
    blur: createProgram(gl, FRAG_BLUR),
    flat: createProgram(gl, FRAG_FLAT),
    vao: createFullscreenVAO(gl),
    targets: {
        grad: createRenderTarget(gl),
        ping: createRenderTarget(gl),
        pong: createRenderTarget(gl),
    },
    poles: createPoleBuffers(HALO_POLES.length),
    size: null,
    t: 0,
});

const onResize = (gl: WebGL2RenderingContext, size: GLSize, s: Scene) => {
    s.size = size;
    sizePassTargets(
        gl,
        s.targets,
        size.width,
        size.height,
        GL_RENDER.gradScale,
        GL_RENDER.blurScale,
    );
};

const onDispose = (gl: WebGL2RenderingContext, s: Scene) => {
    gl.deleteProgram(s.grad.program);
    gl.deleteProgram(s.blur.program);
    gl.deleteProgram(s.flat.program);
    gl.deleteVertexArray(s.vao);
    deleteRenderTarget(gl, s.targets.grad);
    deleteRenderTarget(gl, s.targets.ping);
    deleteRenderTarget(gl, s.targets.pong);
};

/** The prototype's per-frame easing, scaled so it's the same step at 60Hz. */
const easeStep = (rate: number, dt: number) => 1 - Math.pow(1 - rate, dt * 60);

const draw = (
    gl: WebGL2RenderingContext,
    dt: number,
    s: Scene,
    cur: Cursor,
) => {
    if (!s.size) return;
    s.t += dt;
    const { width, height, cssWidth, cssHeight, dpr } = s.size;
    const aspect = cssWidth / cssHeight;

    const follow = easeStep(HALO.ease, dt);
    cur.x += (cur.tx - cur.x) * follow;
    cur.y += (cur.ty - cur.y) * follow;
    cur.amt += (cur.want - cur.amt) * easeStep(HALO.fade, dt);

    simulatePoles(s.t, HALO_POLES, HALO_MOTION, BG, s.poles);

    bindRenderTarget(gl, s.targets.grad);
    gl.useProgram(s.grad.program);
    const g = s.grad.uniforms;
    gl.uniform2f(g.uRes, s.targets.grad.w, s.targets.grad.h);
    gl.uniform1f(g.uTime, s.t);
    gl.uniform1f(g.uAspect, aspect);
    gl.uniform2fv(g.uShapePos, s.poles.shapePos);
    gl.uniform2fv(g.uColorPos, s.poles.colorPos);
    gl.uniform1fv(g.uPoleRad, s.poles.poleRad);
    gl.uniform3fv(g.uPoleCol, s.poles.poleCol);
    gl.uniform1f(g.uScale, HALO.scale);
    // Pinned to the canvas's left edge on desktop, so the mass keeps its
    // distance from it as the section's aspect changes with the width
    gl.uniform1f(
        g.uOffX,
        window.innerWidth <= 1023
            ? HALO.stackedOffsetX
            : -aspect / 2 + HALO.fromLeft,
    );
    gl.uniform1f(g.uOffY, HALO.offsetY);
    const reach = Math.hypot(cur.x, cur.y) || 1e-5;
    gl.uniform2f(g.uStretchDir, cur.x / reach, cur.y / reach);
    gl.uniform1f(g.uStretchAmt, 0);
    gl.uniform1f(g.uShapeWarpAmp, HALO.shapeWarpAmp);
    gl.uniform1f(g.uShapeWarpFreq, HALO.shapeWarpFreq);
    gl.uniform1f(g.uShapeWarpSpeed, HALO.shapeWarpSpeed);
    gl.uniform1f(g.uColorWarpAmp, HALO.colorWarpAmp);
    gl.uniform1f(g.uColorWarpFreq, HALO.colorWarpFreq);
    gl.uniform1f(g.uColorWarpSpeed, HALO.colorWarpSpeed);
    gl.uniform1f(g.uEdgeGain, HALO.edgeGain);
    gl.uniform1f(g.uEdgeShape, HALO.edgeShape);
    gl.uniform1f(g.uFalloff, HALO.falloff);
    gl.uniform1f(g.uColorFalloff, HALO.colorFalloff);
    gl.uniform2f(g.uCurPos, cur.x, cur.y);
    gl.uniform1f(g.uCurAmt, cur.amt);
    gl.uniform1f(g.uCurRadius, HALO.curRadius);
    gl.uniform1f(g.uCurWarp, HALO.curWarp);
    gl.uniform1f(g.uCurSat, HALO.curSat);
    gl.uniform1f(g.uCurHue, HALO.curHue);
    gl.uniform1f(g.uCurSpread, HALO.curSpread);
    drawFullscreen(gl);

    const soft =
        1 + HALO.blurBreath * Math.sin((TAU * s.t) / HALO.blurBreathPeriod);
    const blurred = blurChain(
        gl,
        s.blur,
        s.targets.grad,
        s.targets.ping,
        s.targets.pong,
        HALO.blurPx * soft * dpr * GL_RENDER.blurScale,
        HALO.blurIterations,
    );

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, width, height);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.useProgram(s.flat.program);
    const f = s.flat.uniforms;
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, blurred.tex);
    gl.uniform1i(f.uTex, 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, s.targets.grad.tex);
    gl.uniform1i(f.uSharp, 1);
    gl.activeTexture(gl.TEXTURE0);
    gl.uniform2f(f.uRes, width, height);
    gl.uniform2f(f.uCssRes, cssWidth, cssHeight);
    gl.uniform3fv(f.uBg, BG);
    gl.uniform1f(f.uTime, s.t);
    gl.uniform1f(f.uDither, GL_RENDER.dither);
    gl.uniform1f(f.uAspect, aspect);
    gl.uniform1f(f.uCurAmt, 0);
    drawFullscreen(gl);
};

interface Props {
    /** The section the field fills; the cursor is tracked against its box. */
    hostRef: React.RefObject<HTMLElement | null>;
    className?: string;
}

/**
 * The green/aqua field behind Proof (the prototype's halo.js). Always on; the
 * cursor only shifts its tone where it passes.
 */
export const ProofHalo = (props: Props) => {
    const cursor = React.useRef<Cursor>({
        x: 0,
        y: 0,
        tx: 0,
        ty: 0,
        amt: 0,
        want: 0,
        seen: false,
    });

    React.useEffect(() => {
        const cur = cursor.current;
        const onMove = (event: PointerEvent) => {
            const host = props.hostRef.current;
            if (!host) return;
            const r = host.getBoundingClientRect();
            const w = r.width || 1;
            const h = r.height || 1;
            // p-space: aspect-corrected, centred on the section
            cur.tx = ((event.clientX - r.left) / w - 0.5) * (w / h);
            cur.ty = (event.clientY - r.top) / h - 0.5;
            if (!cur.seen) {
                cur.x = cur.tx;
                cur.y = cur.ty;
                cur.seen = true;
            }
            cur.want =
                event.clientY > r.top &&
                event.clientY < r.bottom &&
                event.clientX > r.left &&
                event.clientX < r.right
                    ? 1
                    : 0;
        };
        const onLeave = () => {
            cur.want = 0;
        };
        window.addEventListener("pointermove", onMove, { passive: true });
        document.documentElement.addEventListener("pointerleave", onLeave);
        window.addEventListener("blur", onLeave);
        return () => {
            window.removeEventListener("pointermove", onMove);
            document.documentElement.removeEventListener(
                "pointerleave",
                onLeave,
            );
            window.removeEventListener("blur", onLeave);
        };
    }, [props.hostRef]);

    const onFrame = (
        gl: WebGL2RenderingContext,
        _now: number,
        dt: number,
        s: Scene,
    ) => draw(gl, dt, s, cursor.current);

    return (
        <GLCanvas
            className={props.className}
            dprMax={HALO.dprMax}
            onInit={onInit}
            onResize={onResize}
            onFrame={onFrame}
            onDispose={onDispose}
        />
    );
};
